import { clsx } from 'clsx'
import {
  Blocks,
  Compass,
  HelpCircle,
  MonitorPlay,
  MousePointerClick,
  NotebookPen,
  Sticker,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import type {
  BuildupStepGradeItem,
  ComponentProgressDetailView,
  OverrideQuizAnswerRequest,
} from '@/types/coaching'
import { useOverrideQuizAnswer } from '@/hooks/coaching/useOverrideQuizAnswer'
import { coachingKeys } from '@/hooks/coaching/useCoachingToday'
import { useGradeBuildupStep } from '@/hooks/coaching/useGradeBuildupStep'
import type { CoachingGradingMode } from '@/hooks/coaching/usePersistedGradingMode'
import { GradingModeSwitch } from '../GradingModeSwitch'
import { PauseQuizGradingSheet } from '../grading-sheet/PauseQuizGradingSheet'
import type { QuizFocusRequest } from '../grading-sheet/coachingFocus'
import { derivePurposeBadge } from './derivePurposeBadge'
import { PurposeBadge } from './parts/PurposeBadge'
import { StatusPill } from './parts/StatusPill'
import { MetaStrip } from './parts/MetaStrip'
import { SkippedFallback } from './parts/SkippedFallback'
import { NotStartedFallback } from './parts/NotStartedFallback'
import { QuizCoachingDetail } from './QuizCoachingDetail'
import { VideoCoachingDetail } from './VideoCoachingDetail'
import { GuideCoachingDetail } from './GuideCoachingDetail'
import { BuildupCoachingDetail } from './BuildupCoachingDetail'
import { WarmupCoachingDetail } from './WarmupCoachingDetail'
import { ToonCoachingDetail } from './ToonCoachingDetail'
import { AssignmentCoachingDetail } from './AssignmentCoachingDetail'
import { InteractiveCoachingDetail } from './InteractiveCoachingDetail'

interface ComponentVisual {
  Icon: LucideIcon
  shellClassName: string
  iconClassName: string
  tone: string
}

const TYPE_VISUAL: Record<ComponentProgressDetailView['type'], ComponentVisual> = {
  video: {
    Icon: MonitorPlay,
    shellClassName: 'border-sky-200 bg-[linear-gradient(145deg,var(--color-sky-50),white_42%,var(--color-cyan-50))]',
    iconClassName: 'bg-sky-600 shadow-[0_3px_0_0_var(--color-sky-700)]',
    tone: 'sky',
  },
  quiz: {
    Icon: HelpCircle,
    shellClassName: 'border-rose-200 bg-[linear-gradient(145deg,var(--color-rose-50),white_42%,var(--color-red-50))]',
    iconClassName: 'bg-rose-500 shadow-[0_3px_0_0_var(--color-rose-700)]',
    tone: 'rose',
  },
  guide: {
    Icon: Compass,
    shellClassName: 'border-blue-200 bg-[linear-gradient(145deg,var(--color-blue-50),white_42%,var(--color-indigo-50))]',
    iconClassName: 'bg-blue-600 shadow-[0_3px_0_0_var(--color-blue-700)]',
    tone: 'blue',
  },
  toon: {
    Icon: Sticker,
    shellClassName: 'border-pink-200 bg-[linear-gradient(145deg,var(--color-pink-50),white_42%,var(--color-fuchsia-50))]',
    iconClassName: 'bg-pink-500 shadow-[0_3px_0_0_var(--color-pink-700)]',
    tone: 'pink',
  },
  assignment: {
    Icon: NotebookPen,
    shellClassName: 'border-emerald-200 bg-[linear-gradient(145deg,var(--color-emerald-50),white_42%,var(--color-teal-50))]',
    iconClassName: 'bg-emerald-600 shadow-[0_3px_0_0_var(--color-emerald-700)]',
    tone: 'emerald',
  },
  interactive: {
    Icon: MousePointerClick,
    shellClassName: 'border-violet-200 bg-[linear-gradient(145deg,var(--color-violet-50),white_42%,var(--color-purple-50))]',
    iconClassName: 'bg-violet-600 shadow-[0_3px_0_0_var(--color-violet-700)]',
    tone: 'violet',
  },
  buildup: {
    Icon: Blocks,
    shellClassName: 'border-amber-200 bg-[linear-gradient(145deg,var(--color-amber-50),white_42%,var(--color-orange-50))]',
    iconClassName: 'bg-amber-500 shadow-[0_3px_0_0_var(--color-amber-700)]',
    tone: 'amber',
  },
  warmup: {
    Icon: Compass,
    shellClassName: 'border-yellow-200 bg-[linear-gradient(145deg,var(--color-yellow-50),white_42%,var(--color-amber-50))]',
    iconClassName: 'bg-yellow-500 shadow-[0_3px_0_0_var(--color-yellow-700)]',
    tone: 'yellow',
  },
}

interface Props {
  component: ComponentProgressDetailView
  index?: number
  learnerId: number
  lessonId: number | null
  canEdit: boolean
  bare?: boolean
  draftResetKey?: number
  onDraftStateChange?: (state: { dirty: boolean; saving: boolean }) => void
  /** 채점/코칭 모드. 기본 coaching(기존 화면). grading이면 pause 영상은 채점표로 렌더한다. */
  onGradingModeChange?: (mode: CoachingGradingMode) => void
  gradingMode?: CoachingGradingMode
  /** 채점표 [답안 편집] → 코칭모드 전환 + 문항 선택 요청 */
  onRequestCoaching?: (quizId: string) => void
  /** 코칭모드로 마운트될 때 미리 선택할 pause 문항 */
  initialSelectedQuizId?: string | null
  focusRequest?: QuizFocusRequest | null
}

export function CoachingComponentDetail({
  component,
  index,
  learnerId,
  lessonId,
  canEdit,
  gradingMode = 'coaching',
  onGradingModeChange,
  onRequestCoaching,
  initialSelectedQuizId,
  focusRequest,
  bare = false,
  draftResetKey,
  onDraftStateChange,
}: Props) {
  const { t } = useTranslation('coaching')
  const purpose = derivePurposeBadge(component)
  const label = component.label || t(`component.type.${component.type}`)
  const { Icon, shellClassName, iconClassName, tone } = TYPE_VISUAL[component.type]

  const override = useOverrideQuizAnswer(learnerId)
  // 채점표는 여러 문항을 순차 저장한 뒤 결과를 1회만 알리므로 토스트 없는 인스턴스를 따로 쓴다.
  const silentOverride = useOverrideQuizAnswer(learnerId, { notify: false, refreshLibrary: false })
  const grade = useGradeBuildupStep(learnerId)
  const onOverride = async (body: Omit<OverrideQuizAnswerRequest, 'lessonId'>) => {
    if (lessonId == null) return // belt-and-suspenders; `editable` below already hides the toggle
    await override.mutateAsync({ ...body, lessonId })
  }
  const onSheetOverride = async (body: Omit<OverrideQuizAnswerRequest, 'lessonId'>) => {
    if (lessonId == null) return
    await silentOverride.mutateAsync({ ...body, lessonId })
  }
  // buildup OXΔ 채점 — quiz override 와 별도 페이로드/핸들러(혼재 금지).
  const onSaveGrades = async (grades: BuildupStepGradeItem[]) => {
    if (lessonId == null || grades.length === 0) return
    await grade.mutateAsync({
      lessonId,
      configComponentId: component.configComponentId,
      grades,
    })
  }
  // Fix 2: without a lessonId we cannot persist an override, so never expose a live toggle
  // (hiding it is cleaner than a dead button that silently drops teacher clicks).
  const editable = canEdit && lessonId != null
  const supportsGradingMode = component.type === 'video' && (component.video?.pausePoints?.length ?? 0) > 0

  const detailBody = (
    <CoachingDetailBody
          component={component}
          learnerId={learnerId}
          canEdit={editable}
          hidePhotoUploads={bare && !editable}
          onOverride={onOverride}
          onSheetOverride={onSheetOverride}
          isSubmitting={override.isPending}
          onSaveGrades={onSaveGrades}
          gradeSubmitting={grade.isPending}
          gradingMode={gradingMode}
          onRequestCoaching={onRequestCoaching}
          initialSelectedQuizId={initialSelectedQuizId}
          focusRequest={focusRequest}
          draftResetKey={draftResetKey}
          onDraftStateChange={onDraftStateChange}
        />
  )
  if (bare) return detailBody

  return (
    <div
      data-testid="coaching-component-shell"
      data-component-tone={tone}
      className={clsx('space-y-4 rounded-xl border p-4 shadow-sm', shellClassName)}
    >
      <header className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={clsx(
                'grid size-9 shrink-0 place-items-center rounded-xl text-white',
                iconClassName,
              )}
            >
              <Icon size={18} aria-hidden="true" />
            </span>
            <span className="truncate text-sm font-bold text-gray-950">{label}</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <PurposeBadge data={purpose} />
            <StatusPill status={component.status} />
            {supportsGradingMode && onGradingModeChange && <GradingModeSwitch mode={gradingMode} onChange={onGradingModeChange} />}
          </div>
        </div>
        <MetaStrip component={component} index={index} />
      </header>
      <div className="rounded-xl border border-white/80 bg-white/95 p-3.5 shadow-sm">
        {detailBody}
      </div>
    </div>
  )
}

function CoachingDetailBody({
  component,
  learnerId,
  canEdit,
  onOverride,
  onSheetOverride,
  isSubmitting,
  onSaveGrades,
  gradeSubmitting,
  hidePhotoUploads,
  gradingMode,
  onRequestCoaching,
  initialSelectedQuizId,
  focusRequest,
  draftResetKey,
  onDraftStateChange,
}: {
  component: ComponentProgressDetailView
  learnerId: number
  canEdit: boolean
  onOverride: (b: Omit<OverrideQuizAnswerRequest, 'lessonId'>) => void | Promise<void>
  onSheetOverride: (b: Omit<OverrideQuizAnswerRequest, 'lessonId'>) => Promise<void>
  isSubmitting: boolean
  onSaveGrades: (grades: BuildupStepGradeItem[]) => Promise<void>
  gradeSubmitting: boolean
  hidePhotoUploads?: boolean
  gradingMode: CoachingGradingMode
  onRequestCoaching?: (quizId: string) => void
  initialSelectedQuizId?: string | null
  focusRequest?: QuizFocusRequest | null
  draftResetKey?: number
  onDraftStateChange?: (state: { dirty: boolean; saving: boolean }) => void
}) {
  const queryClient = useQueryClient()
  if (component.status === 'skipped') {
    // 회차별 정책으로 건너뛴 생각열기(learner_choice) / 콘텐츠 없음 생략(content_missing)은
    // 사유 안내 + 학습자 화면 미러(본문·포인트·회차 음성)를 그대로 보여준다. 이전 레슨 없음(previous_null) 등은 기존 fallback.
    if (
      component.type === 'warmup' &&
      (component.skippedReason === 'learner_choice' || component.skippedReason === 'content_missing')
    ) {
      return <WarmupCoachingDetail component={component} />
    }
    return <SkippedFallback reason={component.skippedReason} />
  }
  // A3 (S0 reachability): quiz/video는 not_started여도 교사가 토글을 열 수 있도록
  // canEdit일 때만 디테일에 도달시킨다. 그 외(canEdit=false 또는 다른 타입)는 fallback.
  // (quiz는 session 토글, video는 pause 토글을 가진다 — 둘 다 canEdit/onOverride/isSubmitting 배선.)
  const editableType = component.type === 'quiz' || component.type === 'video'
  if (component.status === 'not_started' && !(canEdit && editableType)) {
    return <NotStartedFallback type={component.type} />
  }
  switch (component.type) {
    case 'video': {
      // 채점모드: pause point가 있는 영상만 채점표. 없으면(엣지 5) 코칭 화면 그대로.
      const hasPausePoints = (component.video?.pausePoints?.length ?? 0) > 0
      if (gradingMode === 'grading' && hasPausePoints) {
        return (
          <PauseQuizGradingSheet
            component={component}
            canEdit={canEdit}
            onOverride={onSheetOverride}
            onSaveSettled={() => {
              void queryClient.invalidateQueries({ queryKey: coachingKeys.library(learnerId, 'all') })
            }}
            onRequestCoaching={onRequestCoaching}
          />
        )
      }
      return (
        <VideoCoachingDetail
          component={component}
          canEdit={canEdit}
          onOverride={onOverride}
          isSubmitting={isSubmitting}
          initialSelectedQuizId={initialSelectedQuizId}
          focusRequest={focusRequest}
          draftResetKey={draftResetKey}
          onDraftStateChange={onDraftStateChange}
        />
      )
    }
    case 'quiz':        return <QuizCoachingDetail component={component} canEdit={canEdit} onOverride={onOverride} isSubmitting={isSubmitting} draftResetKey={draftResetKey} onDraftStateChange={onDraftStateChange} />
    case 'guide':       return <GuideCoachingDetail component={component} />
    case 'toon':        return <ToonCoachingDetail component={component} />
    case 'assignment':  return <AssignmentCoachingDetail component={component} />
    case 'interactive': return <InteractiveCoachingDetail component={component} />
    case 'buildup':     return <BuildupCoachingDetail component={component} learnerId={hidePhotoUploads ? undefined : learnerId} canEdit={canEdit} onSaveGrades={onSaveGrades} isSubmitting={gradeSubmitting} draftResetKey={draftResetKey} onDraftStateChange={onDraftStateChange} />
    case 'warmup':      return <WarmupCoachingDetail component={component} />
  }
}
